import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import {
  palette,
  status,
  spacing,
  radius,
  shadow,
  typography,
} from '../../style-utils/theme';
import { Tag } from '../pages/dashboard/dashboard-units-components/dashboard-units.styled';
import { getIqUsage } from '../../api/iq-client';
import { IqUsage } from '../../interfaces/iq-interfaces';

// Owner-only Lobbero IQ usage row for the billing screen (WP-8). Aggregates only —
// month-to-date spend vs budget; never rows, building_id or source_ids. Renders
// nothing if IQ is not on for the org (the endpoint 403s / the org is unentitled).
const Row = styled.div`
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.sm};
  padding: ${spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  font-family: ${typography.ui};
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

const Bar = styled.div`
  height: 8px;
  border-radius: ${radius.pill};
  background: ${palette.surfaceMuted};
  overflow: hidden;
`;

const Fill = styled.div<{ $pct: number; $warn: boolean }>`
  height: 100%;
  width: ${p => Math.min(100, Math.max(0, p.$pct * 100))}%;
  background: ${p => (p.$warn ? status.warning.dot : status.brand.dot)};
`;

const Sub = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
`;

const usd = (micros: number): string => `$${(micros / 1_000_000).toFixed(2)}`;

// WP-42. `Math.round(pct * 100)` printed a flat 0% for every real month of
// light use: the value is a 0..1 RATIO, so four cents against a $9.60 budget is
// 0.0042, and rounding that to a whole percent is 0. A usage meter that reads 0%
// while money has been spent looks broken, and it is the one number an owner
// checks to decide whether the AI is affordable.
//
// Precision scales with the magnitude so the digits stay meaningful: two
// decimals under 1%, one under 10%, whole numbers above. Below 0.01% the honest
// answer is a bound, not a number — the backend quantises to five decimals.
export const formatUsagePct = (ratio: number): string => {
  const pct = (Number.isFinite(ratio) ? ratio : 0) * 100;

  if (pct <= 0) return '0%';
  if (pct < 0.01) return '<0.01%';
  if (pct < 1) return `${pct.toFixed(2)}%`;
  if (pct < 10) return `${pct.toFixed(1)}%`;

  return `${Math.round(pct)}%`;
};

export const IqBillingRow: React.FC = () => {
  const [usage, setUsage] = useState<IqUsage | null>(null);

  useEffect(() => {
    let alive = true;
    getIqUsage()
      .then(u => {
        if (alive) setUsage(u);
      })
      .catch(() => {
        // Not entitled / IQ off / not owner — render nothing.
        if (alive) setUsage(null);
      });

    return () => {
      alive = false;
    };
  }, []);

  if (!usage) return null;

  return (
    <Row>
      <Head>
        <span>✦ Lobbero IQ</span>
        {usage.warn ? <Tag tone="warning">Approaching limit</Tag> : null}
        {usage.over ? <Tag tone="alert">Limit reached</Tag> : null}
      </Head>
      <Bar>
        <Fill $pct={usage.pct} $warn={usage.warn} />
      </Bar>
      <Sub>
        {usd(usage.spent_micros)} of {usd(usage.budget_micros)} this month (
        {formatUsagePct(usage.pct)})
      </Sub>
    </Row>
  );
};
